import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Phone, Mail, MapPin, Globe, ChevronRight, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("uk");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // UK Form State
  const [ukForm, setUkForm] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    panel_count: "",
    panel_brand: "",
    location: "",
    message: "",
  });

  // International Form State
  const [intlForm, setIntlForm] = useState({
    name: "",
    company: "",
    country: "",
    phone: "",
    email: "",
    volume_required: "",
    message: "",
  });

  // Korea Form State
  const [koreaForm, setKoreaForm] = useState({
    name: "",
    company: "",
    country: "",
    phone: "",
    email: "",
    message: "",
  });

  useEffect(() => {
    document.title = t("contact.meta.title");
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("contact.meta.description"));
    }
  }, [t]);

  const handleUkSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/enquiries/uk`, ukForm);
      toast.success(t("contact.ukForm.toastSuccess"));
      setUkForm({
        name: "", company: "", phone: "", email: "",
        panel_count: "", panel_brand: "", location: "", message: "",
      });
    } catch {
      toast.error(t("contact.ukForm.toastError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIntlSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/enquiries/international`, intlForm);
      toast.success(t("contact.intlForm.toastSuccess"));
      setIntlForm({
        name: "", company: "", country: "", phone: "",
        email: "", volume_required: "", message: "",
      });
    } catch {
      toast.error(t("contact.intlForm.toastError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKoreaSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/enquiries/korea`, koreaForm);
      toast.success(t("contact.koreaForm.toastSuccess"));
      setKoreaForm({
        name: "", company: "", country: "", phone: "", email: "", message: "",
      });
    } catch {
      toast.error(t("contact.koreaForm.toastError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs = [
    { id: "uk", label: t("contact.tabs.sellUk"), icon: MapPin },
    { id: "international", label: t("contact.tabs.buyerPortal"), icon: Globe },
    { id: "korea", label: t("contact.tabs.korea"), icon: Phone },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 bg-navy text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">
              {t("contact.hero.kicker")}
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold mt-4 mb-6">
              {t("contact.hero.title")} <span className="text-gold">{t("contact.hero.titleAccent")}</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-3xl">
              {t("contact.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-slate-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 p-6 flex items-start gap-4">
              <div className="p-3 bg-navy text-gold">
                <Phone size={20} />
              </div>
              <div>
                <h3 className="font-outfit font-semibold text-navy">{t("contact.infoCards.phoneLabel")}</h3>
                <a href="tel:+447379339449" className="text-slate-600 hover:text-gold transition-colors">
                  +44 7379 339 449
                </a>
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-6 flex items-start gap-4">
              <div className="p-3 bg-navy text-gold">
                <Mail size={20} />
              </div>
              <div>
                <h3 className="font-outfit font-semibold text-navy">{t("contact.infoCards.emailLabel")}</h3>
                <a href="mailto:info@magalaxyintl.com" className="text-slate-600 hover:text-gold transition-colors">
                  info@magalaxyintl.com
                </a>
              </div>
            </div>
            <div className="bg-white border border-slate-200 p-6 flex items-start gap-4">
              <div className="p-3 bg-navy text-gold">
                <Globe size={20} />
              </div>
              <div>
                <h3 className="font-outfit font-semibold text-navy">{t("contact.infoCards.operationsLabel")}</h3>
                <p className="text-slate-600">{t("contact.infoCards.operationsValue")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy">
              {t("contact.formsSection.title")}
            </h2>
            <p className="text-slate-600 mt-4">
              {t("contact.formsSection.subtitle")}
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium uppercase tracking-wider transition-all ${
                  activeTab === tab.id
                    ? "bg-navy text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* UK Form */}
          {activeTab === "uk" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-white border border-slate-200 pt-8 pb-8 ps-8 pe-24 sm:pe-8">
                <h3 className="font-outfit text-xl font-semibold text-navy mb-2">
                  {t("contact.ukForm.title")}
                </h3>
                <p className="text-slate-500 text-sm mb-6">
                  {t("contact.ukForm.subtitle")}
                </p>
                <form onSubmit={handleUkSubmit} data-testid="contact-uk-form">
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder={t("contact.ukForm.namePlaceholder")}
                      value={ukForm.name}
                      onChange={(e) => setUkForm({ ...ukForm, name: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-uk-name"
                    />
                    <input
                      type="text"
                      placeholder={t("contact.ukForm.companyPlaceholder")}
                      value={ukForm.company}
                      onChange={(e) => setUkForm({ ...ukForm, company: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-uk-company"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input
                      type="tel"
                      placeholder={t("contact.ukForm.phonePlaceholder")}
                      value={ukForm.phone}
                      onChange={(e) => setUkForm({ ...ukForm, phone: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-uk-phone"
                    />
                    <input
                      type="email"
                      placeholder={t("contact.ukForm.emailPlaceholder")}
                      value={ukForm.email}
                      onChange={(e) => setUkForm({ ...ukForm, email: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-uk-email"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder={t("contact.ukForm.panelCountPlaceholder")}
                      value={ukForm.panel_count}
                      onChange={(e) => setUkForm({ ...ukForm, panel_count: e.target.value })}
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-uk-panel-count"
                    />
                    <input
                      type="text"
                      placeholder={t("contact.ukForm.panelBrandPlaceholder")}
                      value={ukForm.panel_brand}
                      onChange={(e) => setUkForm({ ...ukForm, panel_brand: e.target.value })}
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-uk-panel-brand"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder={t("contact.ukForm.locationPlaceholder")}
                    value={ukForm.location}
                    onChange={(e) => setUkForm({ ...ukForm, location: e.target.value })}
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 mb-4"
                    data-testid="contact-uk-location"
                  />
                  <textarea
                    placeholder={t("contact.ukForm.messagePlaceholder")}
                    value={ukForm.message}
                    onChange={(e) => setUkForm({ ...ukForm, message: e.target.value })}
                    rows={4}
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 mb-6 resize-none"
                    data-testid="contact-uk-message"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-uk-submit"
                    className="w-full bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? t("contact.ukForm.submittingButton") : t("contact.ukForm.submitButton")}
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {/* International Form */}
          {activeTab === "international" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-white border border-slate-200 pt-8 pb-8 ps-8 pe-24 sm:pe-8">
                <h3 className="font-outfit text-xl font-semibold text-navy mb-2">
                  {t("contact.intlForm.title")}
                </h3>
                <p className="text-slate-500 text-sm mb-6">
                  {t("contact.intlForm.subtitle")}
                </p>
                <form onSubmit={handleIntlSubmit} data-testid="contact-intl-form">
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder={t("contact.intlForm.namePlaceholder")}
                      value={intlForm.name}
                      onChange={(e) => setIntlForm({ ...intlForm, name: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-intl-name"
                    />
                    <input
                      type="text"
                      placeholder={t("contact.intlForm.companyPlaceholder")}
                      value={intlForm.company}
                      onChange={(e) => setIntlForm({ ...intlForm, company: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-intl-company"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder={t("contact.intlForm.countryPlaceholder")}
                      value={intlForm.country}
                      onChange={(e) => setIntlForm({ ...intlForm, country: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-intl-country"
                    />
                    <input
                      type="tel"
                      placeholder={t("contact.intlForm.phonePlaceholder")}
                      value={intlForm.phone}
                      onChange={(e) => setIntlForm({ ...intlForm, phone: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-intl-phone"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input
                      type="email"
                      placeholder={t("contact.intlForm.emailPlaceholder")}
                      value={intlForm.email}
                      onChange={(e) => setIntlForm({ ...intlForm, email: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-intl-email"
                    />
                    <input
                      type="text"
                      placeholder={t("contact.intlForm.volumePlaceholder")}
                      value={intlForm.volume_required}
                      onChange={(e) => setIntlForm({ ...intlForm, volume_required: e.target.value })}
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-intl-volume"
                    />
                  </div>
                  <textarea
                    placeholder={t("contact.intlForm.messagePlaceholder")}
                    value={intlForm.message}
                    onChange={(e) => setIntlForm({ ...intlForm, message: e.target.value })}
                    rows={4}
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 mb-6 resize-none"
                    data-testid="contact-intl-message"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-intl-submit"
                    className="w-full bg-navy text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-navy-900 transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? t("contact.intlForm.submittingButton") : t("contact.intlForm.submitButton")}
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          {/* Korea Form */}
          {activeTab === "korea" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-white border border-slate-200 pt-8 pb-8 ps-8 pe-24 sm:pe-8">
                <h3 className="font-outfit text-xl font-semibold text-navy mb-2">
                  {t("contact.koreaForm.title")}
                </h3>
                <p className="text-slate-500 text-sm mb-6">
                  {t("contact.koreaForm.subtitle")}
                </p>
                <form onSubmit={handleKoreaSubmit} data-testid="contact-korea-form">
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder={t("contact.koreaForm.namePlaceholder")}
                      value={koreaForm.name}
                      onChange={(e) => setKoreaForm({ ...koreaForm, name: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-korea-name"
                    />
                    <input
                      type="text"
                      placeholder={t("contact.koreaForm.companyPlaceholder")}
                      value={koreaForm.company}
                      onChange={(e) => setKoreaForm({ ...koreaForm, company: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-korea-company"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <input
                      type="text"
                      placeholder={t("contact.koreaForm.countryPlaceholder")}
                      value={koreaForm.country}
                      onChange={(e) => setKoreaForm({ ...koreaForm, country: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-korea-country"
                    />
                    <input
                      type="tel"
                      placeholder={t("contact.koreaForm.phonePlaceholder")}
                      value={koreaForm.phone}
                      onChange={(e) => setKoreaForm({ ...koreaForm, phone: e.target.value })}
                      required
                      className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200"
                      data-testid="contact-korea-phone"
                    />
                  </div>
                  <input
                    type="email"
                    placeholder={t("contact.koreaForm.emailPlaceholder")}
                    value={koreaForm.email}
                    onChange={(e) => setKoreaForm({ ...koreaForm, email: e.target.value })}
                    required
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 mb-4"
                    data-testid="contact-korea-email"
                  />
                  <textarea
                    placeholder={t("contact.koreaForm.messagePlaceholder")}
                    value={koreaForm.message}
                    onChange={(e) => setKoreaForm({ ...koreaForm, message: e.target.value })}
                    rows={4}
                    className="form-input w-full px-4 py-3 bg-slate-50 border border-slate-200 mb-6 resize-none"
                    data-testid="contact-korea-message"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-korea-submit"
                    className="w-full bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors disabled:opacity-50"
                  >
                    {isSubmitting ? t("contact.koreaForm.submittingButton") : t("contact.koreaForm.submitButton")}
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("contact.offices.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4">
              {t("contact.offices.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* UK Office */}
            <div className="bg-white border border-slate-200 p-8">
              <div className="inline-block px-4 py-2 bg-navy text-gold text-xs font-bold uppercase tracking-wider mb-6">
                {t("contact.offices.uk.badge")}
              </div>
              <h3 className="font-outfit text-xl font-semibold text-navy mb-4">
                {t("contact.offices.uk.name")}
              </h3>
              <address className="not-italic text-slate-600 leading-relaxed mb-6">
                62 New Road, Newhall<br />
                Swadlincote, Derby<br />
                DE11 0TH, United Kingdom
              </address>
              <div className="space-y-2 text-sm text-slate-500">
                <p>{t("contact.offices.uk.companyRegLabel")} 13671542</p>
                <p>{t("contact.offices.uk.vatLabel")} GB425010549</p>
                <p>{t("contact.offices.uk.eoriLabel")} GB425010549000</p>
                <p>{t("contact.offices.uk.wasteCarrierLabel")} CBDU628298</p>
              </div>
            </div>

            {/* Korea Office */}
            <div className="bg-navy text-white p-8">
              <div className="inline-block px-4 py-2 bg-gold text-navy text-xs font-bold uppercase tracking-wider mb-6">
                {t("contact.offices.korea.badge")}
              </div>
              <h3 className="font-outfit text-xl font-semibold mb-4">
                {t("contact.offices.korea.name")}
              </h3>
              <address className="not-italic text-slate-300 leading-relaxed mb-6">
                4385-23 Seodong-daero<br />
                Gongdo-eup, Anseong<br />
                Gyeonggi-do, South Korea
              </address>
              <div className="space-y-2 text-sm text-slate-400">
                <p>{t("contact.offices.korea.foundedLabel")} 2000</p>
                <p>{t("contact.offices.korea.yearsExperienceText")}</p>
                <p>{t("contact.offices.korea.containersShippedText")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
